import { useState } from 'react'
import { Link } from 'react-router'
import { BrandService } from '../../client'
import { apiErrorCode, apiErrorMessage } from '../../lib/api'
import { useAuth } from '../../providers/auth/useAuth'
import Modal from '../ui/Modal'

const INPUT =
  'w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-900 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 focus:outline-none'

export default function AddBrandDialog({ onClose }: { onClose: () => void }) {
  const { refreshAccount, selectBrand } = useAuth()
  const [name, setName] = useState('')
  const [website, setWebsite] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<{ message: string; atLimit: boolean } | null>(null)

  async function create() {
    if (!name.trim()) return
    setSaving(true)
    setError(null)
    try {
      const brand = await BrandService.createBrand({ name: name.trim(), website: website.trim() || null })
      await refreshAccount()
      selectBrand(brand.id)
      onClose()
    } catch (err) {
      setError({ message: apiErrorMessage(err), atLimit: apiErrorCode(err) === 'brand_limit_reached' })
      setSaving(false)
    }
  }

  return (
    <Modal title="Add a brand" width="max-w-sm" onClose={onClose}>
      <label className="block text-xs font-medium text-stone-600">
        Brand name
        <input autoFocus value={name} onChange={(event) => setName(event.target.value)} className={`${INPUT} mt-1.5`} />
      </label>
      <label className="mt-3 block text-xs font-medium text-stone-600">
        Website
        <input
          value={website}
          placeholder="https://"
          onChange={(event) => setWebsite(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void create()
          }}
          className={`${INPUT} mt-1.5`}
        />
      </label>

      {error && (
        <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
          {error.message}{' '}
          {error.atLimit && (
            <Link to="/plans" onClick={onClose} className="font-medium underline underline-offset-2">
              See plans
            </Link>
          )}
        </p>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 transition hover:bg-stone-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={saving || !name.trim()}
          onClick={() => void create()}
          className="rounded-lg bg-purple-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-purple-800 disabled:opacity-50"
        >
          {saving ? 'Adding…' : 'Add brand'}
        </button>
      </div>
    </Modal>
  )
}
